import './coapplicantEditIncomePage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode, useMemo } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { Select } from '../../../components/Select/Select';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { additionalIncomeTypeOptions } from '../../../store/applicantStore/constants/additionalIncomeTypeOptions';
import { incomeFrequencyOptions } from '../../../store/applicantStore/constants/incomeFrequencyOptions';
import { incomeTypeOptions } from '../../../store/applicantStore/constants/incomeTypeOptions';
import { IncomeTypeEnum } from '../../../store/applicantStore/enums/IncomeTypeEnum';
import { YES } from '../../../store/applicantStore/types/YesOrNo';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { coapplicantReviewEditPathname, coapplicantReviewEditProgress },
    coapplicantAdditionalIncomeAmount,
    coapplicantAdditionalIncomeDurationMonths,
    coapplicantAdditionalIncomeDurationYears,
    coapplicantAdditionalIncomeEmployerName,
    coapplicantAdditionalIncomeFrequency,
    coapplicantAdditionalIncomeJobTitle,
    coapplicantAdditionalIncomeType,
    hasAdditionalIncome,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantAdditionalIncomeAmount,
    setCoapplicantAdditionalIncomeDurationMonths,
    setCoapplicantAdditionalIncomeDurationYears,
    setCoapplicantAdditionalIncomeEmployerName,
    setCoapplicantAdditionalIncomeFrequency,
    setCoapplicantAdditionalIncomeJobTitle,
    setCoapplicantAdditionalIncomeType,
    setHasAdditionalIncome,
    syncCoapplicant,
  } = useCoapplicantStore();
  const {
    clearErrors,
    control,
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
    defaultValues: {
      hasAdditionalIncomeValue: hasAdditionalIncome,
      incomeTypeValue: coapplicantAdditionalIncomeType,
      incomeFrequencyValue: coapplicantAdditionalIncomeFrequency,
      incomeAmountValue: coapplicantAdditionalIncomeAmount,
      incomeEmployerNameValue: coapplicantAdditionalIncomeEmployerName,
      incomeJobTitleValue: coapplicantAdditionalIncomeJobTitle,
      incomeDurationYearsValue: coapplicantAdditionalIncomeDurationYears,
      incomeDurationMonthsValue: coapplicantAdditionalIncomeDurationMonths,
    },
  });

  const shouldShowIncomeFields = watch('hasAdditionalIncomeValue') === YES;

  const additionalIncomeValidation = {
    required: t('validation.additionalIncome.required'),
  };

  const handleAdditionalIncomeChange = (value: string): void => {
    if (value !== YES) {
      clearErrors([
        'incomeAmountValue',
        'incomeEmployerNameValue',
        'incomeJobTitleValue',
      ]);
    }
  };

  const incomeTypeValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.incomeType.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeFrequencyValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.incomeFrequency.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeAmountValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return { min: undefined, required: false, setValueAs: undefined };
    }

    return {
      setValueAs: (value: string): string =>
        inputEventHandler.currency.parser(value),
      required: t('validation.incomeAmount.required'),
      min: {
        value: 0,
        message: t('validation.incomeAmount.min', { min: '$0' }),
      },
    };
  }, [shouldShowIncomeFields]);

  const incomeEmployerNameValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return { required: false };
    }

    return {
      required: t('validation.employerName.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeJobTitleValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return { required: false };
    }

    return {
      required: t('validation.jobTitle.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeDurationYearsValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.durationYears.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeDurationMonthsValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.durationMonths.required'),
    };
  }, [shouldShowIncomeFields]);

  const showSecondaryEmploymentFields =
    watch('incomeTypeValue') === IncomeTypeEnum.SECONDARY_EMPLOYMENT;

  const finishEditing = (): void => {
    navigate(coapplicantReviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      hasAdditionalIncomeValue,
      incomeAmountValue,
      incomeDurationMonthsValue,
      incomeDurationYearsValue,
      incomeEmployerNameValue,
      incomeFrequencyValue,
      incomeJobTitleValue,
      incomeTypeValue,
    } = values;

    setHasAdditionalIncome(hasAdditionalIncomeValue);

    if (shouldShowIncomeFields) {
      setCoapplicantAdditionalIncomeAmount(incomeAmountValue);
      setCoapplicantAdditionalIncomeFrequency(incomeFrequencyValue);
      setCoapplicantAdditionalIncomeType(incomeTypeValue);

      if (showSecondaryEmploymentFields) {
        setCoapplicantAdditionalIncomeDurationMonths(incomeDurationMonthsValue);
        setCoapplicantAdditionalIncomeDurationYears(incomeDurationYearsValue);
        setCoapplicantAdditionalIncomeEmployerName(incomeEmployerNameValue);
        setCoapplicantAdditionalIncomeJobTitle(incomeJobTitleValue);
      } else {
        setCoapplicantAdditionalIncomeDurationMonths(null);
        setCoapplicantAdditionalIncomeDurationYears(null);
        setCoapplicantAdditionalIncomeEmployerName(null);
        setCoapplicantAdditionalIncomeJobTitle(null);
      }
    } else {
      setCoapplicantAdditionalIncomeAmount(null);
      setCoapplicantAdditionalIncomeFrequency(null);
      setCoapplicantAdditionalIncomeType(null);
      setCoapplicantAdditionalIncomeDurationMonths(null);
      setCoapplicantAdditionalIncomeDurationYears(null);
      setCoapplicantAdditionalIncomeEmployerName(null);
      setCoapplicantAdditionalIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-edit-income-page" sx={theme.pageLayout}>
        <Header
          progress={coapplicantReviewEditProgress}
          showDrawerToggle
          onBackClick={finishEditing}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantEditIncome.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantEditIncome.form.h1}>
                {t('CoapplicantEditIncomePage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantEditIncome.form.h2}>
                {t('CoapplicantEditIncomePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantEditIncome.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    control={control}
                    errorTestId={
                      locator.pages.coapplicantEditIncome.form
                        .hasAdditionalIncomeSelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantEditIncome.form
                        .hasAdditionalIncomeSelect
                    }
                    label={t('CoapplicantEditIncomePage.additionalIncome')}
                    name="hasAdditionalIncomeValue"
                    options={additionalIncomeTypeOptions}
                    rules={additionalIncomeValidation}
                    onChange={handleAdditionalIncomeChange}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    control={control}
                    disabled={!shouldShowIncomeFields}
                    errorTestId={
                      locator.pages.coapplicantEditIncome.form
                        .incomeTypeSelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantEditIncome.form.incomeTypeSelect
                    }
                    label={t('CoapplicantEditIncomePage.incomeType')}
                    name="incomeTypeValue"
                    options={incomeTypeOptions}
                    rules={incomeTypeValidation}
                  />
                </div>

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'incomeEmployerNameValue',
                        incomeEmployerNameValidation,
                      )}
                      disabled={!shouldShowIncomeFields}
                      error={!isEmpty(errors.incomeEmployerNameValue)}
                      label={t('CoapplicantEditIncomePage.employerName')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.coapplicantEditIncome.form
                              .employerNameInput,
                        },
                      }}
                      value={
                        shouldShowIncomeFields
                          ? watch(
                              'incomeEmployerNameValue',
                              coapplicantAdditionalIncomeEmployerName || '',
                            ) || ''
                          : ''
                      }
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.incomeEmployerNameValue) && (
                      <span
                        className="field-error"
                        data-testid={
                          locator.pages.coapplicantEditIncome.form
                            .employerNameInputError
                        }
                      >
                        {toString(errors.incomeEmployerNameValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'incomeJobTitleValue',
                        incomeJobTitleValidation,
                      )}
                      disabled={!shouldShowIncomeFields}
                      error={!isEmpty(errors.incomeJobTitleValue)}
                      label={t('CoapplicantEditIncomePage.jobTitle')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.coapplicantEditIncome.form
                              .jobTitleInput,
                        },
                      }}
                      value={
                        shouldShowIncomeFields
                          ? watch(
                              'incomeJobTitleValue',
                              coapplicantAdditionalIncomeJobTitle || '',
                            ) || ''
                          : ''
                      }
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.incomeJobTitleValue) && (
                      <span
                        className="field-error"
                        data-testid={
                          locator.pages.coapplicantEditIncome.form
                            .jobTitleInputError
                        }
                      >
                        {toString(errors.incomeJobTitleValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div
                    className="inline-inputs"
                    style={{ marginTop: '1.5rem' }}
                  >
                    <div className="input-half">
                      <Select
                        control={control}
                        disabled={!shouldShowIncomeFields}
                        errorTestId={
                          locator.pages.coapplicantEditIncome.form
                            .yearSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantEditIncome.form.yearSelect
                        }
                        label={t('CoapplicantEditIncomePage.durationYears')}
                        name="incomeDurationYearsValue"
                        options={Array.from({ length: 11 }, (_, index) => ({
                          name: String(index),
                          value: index,
                        }))}
                        rules={incomeDurationYearsValidation}
                      />
                    </div>

                    <div className="input-half">
                      <Select
                        control={control}
                        disabled={!shouldShowIncomeFields}
                        errorTestId={
                          locator.pages.coapplicantEditIncome.form
                            .monthSelectError
                        }
                        inputTestId={
                          locator.pages.coapplicantEditIncome.form.monthSelect
                        }
                        label={t('CoapplicantEditIncomePage.durationMonths')}
                        name="incomeDurationMonthsValue"
                        options={Array.from({ length: 12 }, (_, index) => ({
                          name: String(index),
                          value: index,
                        }))}
                        rules={incomeDurationMonthsValidation}
                      />
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    control={control}
                    disabled={!shouldShowIncomeFields}
                    errorTestId={
                      locator.pages.coapplicantEditIncome.form
                        .frequencySelectError
                    }
                    inputTestId={
                      locator.pages.coapplicantEditIncome.form.frequencySelect
                    }
                    label={t('CoapplicantEditIncomePage.incomeFrequency')}
                    name="incomeFrequencyValue"
                    options={incomeFrequencyOptions}
                    rules={incomeFrequencyValidation}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('incomeAmountValue', incomeAmountValidation)}
                    disabled={!shouldShowIncomeFields}
                    error={
                      shouldShowIncomeFields &&
                      !isEmpty(errors.incomeAmountValue)
                    }
                    label={t('CoapplicantEditIncomePage.incomeAmount')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.coapplicantEditIncome.form.amountInput,
                        inputMode: 'numeric',
                      },
                    }}
                    type="text"
                    value={
                      shouldShowIncomeFields
                        ? inputEventHandler.currency.formatter(
                            watch(
                              'incomeAmountValue',
                              coapplicantAdditionalIncomeAmount,
                            ),
                          )
                        : ''
                    }
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'incomeAmountValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'incomeAmountValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'incomeAmountValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {shouldShowIncomeFields &&
                    !isEmpty(errors.incomeAmountValue) && (
                      <span
                        className="field-error"
                        data-testid={
                          locator.pages.coapplicantEditIncome.form
                            .amountInputError
                        }
                      >
                        {toString(errors.incomeAmountValue.message)}
                      </span>
                    )}
                </div>
              </div>
              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <Button
                    color="secondary"
                    data-testid={
                      locator.pages.coapplicantEditIncome.form.cancelButton
                    }
                    loadingPosition="start"
                    startIcon={<></>}
                    type="button"
                    variant="contained"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('CoapplicantEditIncomePage.cancel')}
                  </Button>

                  <Button
                    color="primary"
                    data-testid={
                      locator.pages.coapplicantEditIncome.form.submitButton
                    }
                    disabled={!isValid}
                    loading={isSyncingCoapplicant}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('CoapplicantEditIncomePage.save')}
                  </Button>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantEditIncome.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEditIncomePage';
